import type {DragEndEvent} from '@dnd-kit/core';
import {arrayMove} from '@dnd-kit/sortable';
import {useEffect, useState} from 'react';
import type {GetEntryManagementResponse} from '../../../core/repositories/mypageEntryManagementsRepository';

type EntryManagementDetailStep = NonNullable<
  GetEntryManagementResponse['steps']
>[number];

type UseStepReorderingProps = {
  steps: EntryManagementDetailStep[];
  onSaveStepOrder?: (steps: EntryManagementDetailStep[]) => Promise<void>;
};

export const useProgressStepReordering = ({
  steps,
  onSaveStepOrder,
}: UseStepReorderingProps) => {
  const [isEditMode, setIsEditMode] = useState(true);
  const [reorderedSteps, setReorderedSteps] =
    useState<EntryManagementDetailStep[]>(steps);
  const [hasChanges, setHasChanges] = useState(false);

  // steps が変更されたら reorderedSteps を更新
  useEffect(() => {
    setReorderedSteps(steps);
    setHasChanges(false);
  }, [steps]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const {active, over} = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = reorderedSteps.findIndex((item) => item.id === active.id);
    const newIndex = reorderedSteps.findIndex((item) => item.id === over.id);

    const newItems = arrayMove(reorderedSteps, oldIndex, newIndex);

    // stepOrderを振り直す
    const updatedSteps = newItems.map((item, index) => ({
      ...item,
      stepOrder: index + 1,
    }));

    setReorderedSteps(updatedSteps);

    // 自動保存
    if (onSaveStepOrder) {
      await onSaveStepOrder(updatedSteps);
    }
  };

  const handleSaveOrder = async () => {
    if (onSaveStepOrder && hasChanges) {
      await onSaveStepOrder(reorderedSteps);
      setHasChanges(false);
      setIsEditMode(false);
    }
  };

  const handleCancelEdit = () => {
    setReorderedSteps(steps);
    setHasChanges(false);
    setIsEditMode(false);
  };

  const handleToggleEditMode = () => {
    if (isEditMode) {
      handleCancelEdit();
    } else {
      setIsEditMode(true);
    }
  };

  /**
   * ステップの順序を入れ替えて保存する共通処理
   * @param fromIndex - 移動元のインデックス
   * @param toIndex - 移動先のインデックス
   */
  const moveStepAndSave = async (fromIndex: number, toIndex: number) => {
    const newItems = arrayMove(reorderedSteps, fromIndex, toIndex);
    const updatedSteps = newItems.map((item, index) => ({
      ...item,
      stepOrder: index + 1,
    }));

    setReorderedSteps(updatedSteps);
    if (onSaveStepOrder) {
      await onSaveStepOrder(updatedSteps);
    }
  };

  /**
   * 指定したステップを1つ上に移動
   */
  const handleMoveUp = async (stepId: number) => {
    const currentIndex = reorderedSteps.findIndex((item) => item.id === stepId);
    if (currentIndex <= 0) return;

    await moveStepAndSave(currentIndex, currentIndex - 1);
  };

  /**
   * 指定したステップを1つ下に移動
   */
  const handleMoveDown = async (stepId: number) => {
    const currentIndex = reorderedSteps.findIndex((item) => item.id === stepId);
    if (currentIndex === -1 || currentIndex >= reorderedSteps.length - 1)
      return;

    await moveStepAndSave(currentIndex, currentIndex + 1);
  };

  return {
    isOrderEditMode: isEditMode,
    reorderedSteps,
    hasChanges,
    handleDragEnd,
    handleSaveOrder,
    handleCancelEdit,
    handleToggleEditMode,
    handleMoveUp,
    handleMoveDown,
  };
};
